/**
 * @module core/api/file-api
 * @description File-specific API operations including viewing, downloading,
 * deleting, uploading files, and managing file categories.
 */

import { get, del, handleResponse, stripEntityPrefix, getBaseUrl } from './api-client.js';
import { getValidToken } from '../auth/token-manager.js';
import { ENTITY_TYPES } from '../../features/entity/entity-types.js';
import { logger } from '../../utils/logger.js';

/**
 * Get file data by archived file object ID
 * Used for retrieving file metadata and download URLs
 *
 * @param {string} fileId - File ID (may include entity prefix like "project_12345")
 * @param {string} entityType - Entity type (projects, opportunities, leads, contacts)
 * @returns {Promise<Object>} File data including download URL and metadata
 * @throws {Error} If request fails or file not found
 *
 * @example
 * const fileData = await getFileByArchivedFileObjectId('project_12345', 'projects');
 * window.open(fileData.downloadUrl, '_blank');
 */
export async function getFileByArchivedFileObjectId(fileId, entityType = 'projects') {
    if (!fileId) {
        throw new Error('File ID is required');
    }

    // Get entity configuration
    const config = ENTITY_TYPES[entityType];

    // Determine which API endpoint to use (searchEntityType or entityType)
    const apiEntityType = config?.searchEntityType || entityType;

    // Strip entity prefix if present (e.g., "project_12345" -> "12345")
    const cleanFileId = stripEntityPrefix(fileId);

    const response = await get(`/api/${apiEntityType}/archivedfiles/${cleanFileId}`);
    return await handleResponse(response, 'Failed to get file data');
}

/**
 * Delete a file from an entity
 *
 * @param {string} fileId - File ID (may include entity prefix)
 * @param {string} entityType - Entity type (projects, opportunities, leads, contacts)
 * @param {string} entityId - ID of the parent entity
 * @returns {Promise<Object>} Deletion result with success status and any warnings
 * @throws {Error} If deletion fails
 *
 * @example
 * const result = await deleteFile('project_12345', 'projects', '98765');
 * if (result.Errors && result.Errors.length > 0) {
 *   console.warn('Warnings:', result.Errors);
 * }
 */
export async function deleteFile(fileId, entityType, entityId) {
    if (!fileId || !entityType || !entityId) {
        throw new Error('File ID, entity type, and entity ID are required');
    }

    // Get entity configuration
    const config = ENTITY_TYPES[entityType];

    // Determine which API endpoint to use (searchEntityType or entityType)
    const apiEntityType = config?.searchEntityType || entityType;

    // Strip entity prefix if present
    const cleanFileId = stripEntityPrefix(fileId);

    const response = await del(`/api/${apiEntityType}/${entityId}/files/${cleanFileId}`);

    if (!response.ok) {
        let errorMessage = 'Failed to delete file';

        try {
            const errorData = await response.json();
            if (errorData.Details) {
                errorMessage = errorData.Details;
            } else if (errorData.Error) {
                errorMessage = errorData.Error;
            }
        } catch {
            // Fallback to status text if JSON parsing fails
            errorMessage = `${response.status}: ${response.statusText}`;
        }

        throw new Error(errorMessage);
    }

    return await response.json();
}

/**
 * Upload files to an entity
 * Handles multi-file uploads with category assignment
 *
 * @param {FormData} formData - FormData object containing files and categories
 * @param {string} entityType - Entity type (projects, opportunities, leads, contacts)
 * @param {string} entityId - ID of the parent entity
 * @returns {Promise<Object>} Upload result with success/failure counts
 * @throws {Error} If upload fails
 *
 * @example
 * const formData = new FormData();
 * formData.append('file1', fileBlob);
 * formData.append('file1_category', 'categoryId');
 *
 * const result = await uploadFiles(formData, 'projects', '12345');
 * console.log(`Uploaded ${result.successCount} of ${result.totalFiles} files`);
 */
export async function uploadFiles(formData, entityType, entityId) {
    if (!formData || !entityType || !entityId) {
        throw new Error('FormData, entity type, and entity ID are required');
    }

    if (!(formData instanceof FormData)) {
        throw new Error('First parameter must be a FormData object');
    }

    // Get entity configuration
    const config = ENTITY_TYPES[entityType];

    // Determine which API endpoint to use (searchEntityType or entityType)
    const apiEntityType = config?.searchEntityType || entityType;

    try {
        // Get fresh token for upload
        const token = await getValidToken();

        // Send request - Note: We can't use fetchWithAuth for FormData
        // because the Content-Type must be set by the browser with boundary
        const makeUploadRequest = async (authToken) => fetch(`${getBaseUrl()}/api/${apiEntityType}/${entityId}/files`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authToken}`
                // Don't set Content-Type - browser will set it with boundary for FormData
            },
            body: formData
        });

        let response = await makeUploadRequest(token);

        // If token expired, refresh and retry
        if (response.status === 401) {
            logger.debug('Upload token expired, refreshing...');
            const newToken = await getValidToken(true);
            response = await makeUploadRequest(newToken);
        }

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');

            if (response.status === 404) {
                throw new Error('Upload endpoint not found. The upload functionality may not be implemented on the server.');
            } else if (response.status === 405) {
                throw new Error('Upload method not allowed. The server may not support file uploads for this endpoint.');
            } else {
                throw new Error(`Upload failed: ${response.status} ${response.statusText}. Server response: ${errorText}`);
            }
        }

        const result = await response.json();

        // Validate response format
        if (result && typeof result.totalFiles === 'number') {
            return {
                successCount: result.successCount || 0,
                totalFiles: result.totalFiles || 0,
                failureCount: result.failureCount || (result.totalFiles - (result.successCount || 0)),
                errors: result.errors || []
            };
        } else {
            throw new Error('Server returned unexpected response format');
        }

    } catch (error) {
        logger.error('Error uploading files:', error);
        throw error;
    }
}

/**
 * Get all available file categories
 * Categories are used to classify uploaded files
 *
 * @returns {Promise<Array>} Array of file category objects
 * @throws {Error} If request fails
 *
 * @example
 * const categories = await getFileCategories();
 * categories.forEach(cat => {
 *   console.log(`${cat.id}: ${cat.name}`);
 * });
 */
export async function getFileCategories() {
    try {
        const response = await get('/api/file-categories');
        const data = await handleResponse(response, 'Failed to fetch file categories');

        // Handle both possible response formats:
        // Format 1: { categories: [...] }
        // Format 2: [...]
        let categories = [];

        if (Array.isArray(data)) {
            categories = data;
        } else if (data.categories && Array.isArray(data.categories)) {
            categories = data.categories;
        } else {
            logger.warn('Invalid file categories response format');
            categories = [];
        }

        // Additional validation
        if (!Array.isArray(categories)) {
            categories = [];
        }

        return categories;

    } catch (error) {
        logger.error('Error fetching file categories:', error);
        throw error;
    }
}

/**
 * Download a file
 * Opens the file in a new tab or triggers download based on browser behavior
 *
 * @param {string} fileId - File ID (may include entity prefix)
 * @param {string} fileName - Original file name
 * @param {string} entityType - Entity type (projects, opportunities, leads, contacts)
 * @returns {Promise<void>}
 * @throws {Error} If file cannot be retrieved
 *
 * @example
 * await downloadFile('project_12345', 'document.pdf', 'projects');
 */
export async function downloadFile(fileId, fileName, entityType = 'projects') {
    try {
        // Get file data which includes the download URL
        const fileData = await getFileByArchivedFileObjectId(fileId, entityType);

        if (!fileData.downloadUrl) {
            throw new Error('No download URL returned from server');
        }

        // Open in new tab - browser will handle download/view based on content type
        const link = document.createElement('a');
        link.href = fileData.downloadUrl;
        link.target = '_blank';
        link.download = fileName; // Suggest filename for download
        link.click();

    } catch (error) {
        logger.error('Error downloading file:', error);
        throw error;
    }
}

/**
 * View a file in browser (if supported content type)
 *
 * @param {string} fileId - File ID (may include entity prefix)
 * @param {string} entityType - Entity type (projects, opportunities, leads, contacts)
 * @returns {Promise<string>} File URL opened in new tab
 * @throws {Error} If file cannot be retrieved or viewed
 *
 * @example
 * const url = await viewFile('project_12345', 'projects');
 * console.log('File opened at:', url);
 */
export async function viewFile(fileId, entityType = 'projects') {
    try {
        // Get file data which includes the download URL
        const fileData = await getFileByArchivedFileObjectId(fileId, entityType);

        if (!fileData.downloadUrl) {
            throw new Error('No download URL returned from server');
        }

        // Open in new tab for viewing
        window.open(fileData.downloadUrl, '_blank');

        return fileData.downloadUrl;

    } catch (error) {
        logger.error('Error viewing file:', error);
        throw error;
    }
}

/**
 * Check if a file can be previewed in browser based on content type
 *
 * @param {string} contentType - MIME type of the file
 * @returns {boolean} True if file can be viewed in browser
 *
 * @example
 * if (canPreviewFile('application/pdf')) {
 *   // Show preview button
 * }
 */
export function canPreviewFile(contentType) {
    if (!contentType) return false;

    const viewableTypes = [
        'image/', 'application/pdf', 'text/', 'audio/', 'video/',
        'application/json', 'application/xml'
    ];

    return viewableTypes.some(type =>
        contentType.toLowerCase().startsWith(type.toLowerCase())
    );
}

/**
 * Get file metadata including size, content type, and creation date
 * Lightweight alternative to getFileByArchivedFileObjectId when full data not needed
 *
 * @param {string} fileId - File ID (may include entity prefix)
 * @param {string} entityType - Entity type
 * @returns {Promise<Object>} File metadata (without download URL)
 * @throws {Error} If request fails
 */
export async function getFileMetadata(fileId, entityType = 'projects') {
    if (!fileId) {
        throw new Error('File ID is required');
    }

    // Get entity configuration
    const config = ENTITY_TYPES[entityType];

    // Determine which API endpoint to use (searchEntityType or entityType)
    const apiEntityType = config?.searchEntityType || entityType;

    const cleanFileId = stripEntityPrefix(fileId);
    const response = await get(`/api/${apiEntityType}/archivedfiles/${cleanFileId}/metadata`);
    return await handleResponse(response, 'Failed to get file metadata');
}
